import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gnf = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} GNF`;
const short = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M` : `${Math.round(v / 1e3)} k`);

const Trend = ({ t }) => {
  const Icon = t.direction === 'up' ? TrendingUp : t.direction === 'down' ? TrendingDown : Minus;
  const color = t.direction === 'up' ? 'text-red-600' : t.direction === 'down' ? 'text-green-600' : 'text-gray-500';
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="border rounded-md p-4" data-testid="collection-outstanding">
        <p className="text-xs text-gray-500">Reste à recouvrer (fin du mois)</p>
        <p className="text-2xl font-bold">{gnf(t.current)}</p>
        <p className={`text-sm flex items-center gap-1 ${color}`} data-testid="collection-trend">
          <Icon className="w-4 h-4" /> {t.delta > 0 ? '+' : ''}{gnf(t.delta)} vs mois précédent
        </p>
      </div>
      <div className="border rounded-md p-4">
        <p className="text-xs text-gray-500">Encaissé ce mois-ci</p>
        <p className="text-2xl font-bold text-green-700">{gnf(t.collected_this_month)}</p>
        <p className="text-sm text-gray-500">Mois précédent : {gnf(t.collected_last_month)}</p>
      </div>
      <div className="border rounded-md p-4">
        <p className="text-xs text-gray-500">Lecture</p>
        <p className="text-sm text-gray-700">{t.direction === 'up' ? 'Le reste à recouvrer augmente : pensez aux relances.' : t.direction === 'down' ? 'Le recouvrement progresse.' : 'Reste à recouvrer stable.'}</p>
      </div>
    </div>
  );
};

const ClassTable = ({ rows }) => (
  <div className="overflow-x-auto" data-testid="collection-class-table">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50"><tr>{['Classe', 'Élèves', 'Facturé', 'Encaissé', 'Reste à payer', 'Dont en retard', 'Taux'].map((h) => (
        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>))}</tr></thead>
      <tbody className="divide-y">
        {rows.map((c) => (
          <tr key={c.class_id || 'none'}>
            <td className="px-3 py-2 font-medium">{c.class_name}</td>
            <td className="px-3 py-2">{c.students}</td>
            <td className="px-3 py-2">{gnf(c.billed)}</td>
            <td className="px-3 py-2 text-green-700">{gnf(c.collected)}</td>
            <td className="px-3 py-2 font-semibold">{gnf(c.outstanding)}</td>
            <td className="px-3 py-2 text-red-600">{gnf(c.overdue)}</td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-200 rounded"><div className="h-2 bg-green-600 rounded" style={{ width: `${Math.min(c.rate || 0, 100)}%` }} /></div>
                {c.rate != null ? `${c.rate} %` : '-'}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CollectionDashboard = () => {
  const [months, setMonths] = useState('12');
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/finance/collection-dashboard`, { params: { months } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [months]);
  if (!data) return null;
  return (
    <Card data-testid="collection-dashboard">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Tableau de recouvrement</CardTitle>
          <CardDescription>Facturé (échéances du mois), encaissé (paiements du mois) et reste à recouvrer en fin de mois.</CardDescription>
        </div>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="w-36" data-testid="collection-months-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="6">6 mois</SelectItem><SelectItem value="12">12 mois</SelectItem><SelectItem value="24">24 mois</SelectItem></SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-6">
        <Trend t={data.trend} />
        <div className="h-80" data-testid="collection-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.series} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" fontSize={12} />
              <YAxis tickFormatter={short} fontSize={12} width={60} />
              <Tooltip formatter={(v) => gnf(v)} />
              <Legend />
              <Bar dataKey="billed" name="Facturé" fill="#93c5fd" radius={[3, 3, 0, 0]} />
              <Bar dataKey="collected" name="Encaissé" fill="#16a34a" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="outstanding" name="Reste à recouvrer" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <ClassTable rows={data.classes} />
      </CardContent>
    </Card>
  );
};

export default CollectionDashboard;
